import { useState, useEffect, Dispatch, SetStateAction } from 'react';

import { Autocomplete, AutocompleteProps, Box } from '@mui/material';

import { get } from '../../api/api';
import SkeletonInput from '../SkeletonInput';

interface InputSelectProps {
  name: string;
  remotedata?: string;
  selectedvalue?: any;
  defaultValue?: string | number;
  fieldid?: string;
  refreshinput?: boolean;
  setrefreshinput?: Dispatch<SetStateAction<boolean>>;
  onSelectedValue?: (value: any) => Promise<void>;
}

export default function InputSelect<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & InputSelectProps) {
  const {
    name,
    remotedata,
    selectedvalue,
    options,
    fieldid,
    refreshinput,
    setrefreshinput,
    onSelectedValue,
    ...rest
  } = props;
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selected, setSelected] = useState<any>({});

  const fetchData = () => {
    setLoading(true);

    if (remotedata) {
      get(remotedata).then((res) => {
        const resData = res.data.data;
        setData(resData);

        if (selectedvalue) {
          if (typeof selectedvalue === 'object') {
            setSelected(selectedvalue);
          } else {
            const selectedDefaultValue = resData.filter(
              (item: any) => item[fieldid ?? name] == selectedvalue,
            )[0];

            setSelected(selectedDefaultValue);
          }
        }
        setLoading(false);
      });
    } else {
      setSelected(selectedvalue);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [remotedata]);

  useEffect(() => {
    if (refreshinput) {
      fetchData();
      setrefreshinput && setrefreshinput(false);
    }
  }, [refreshinput]);

  useEffect(() => {
    if (typeof selectedvalue === 'object') {
      setSelected(selectedvalue);
    } else {
      const selectedDefaultValue = data.filter(
        (item: any) => item[fieldid ?? name] == selectedvalue,
      )[0];

      setSelected(selectedDefaultValue);
    }
  }, [selectedvalue]);

  useEffect(() => {
    if (!loading && onSelectedValue) {
      onSelectedValue(selected);
    }
  }, [selected]);

  const handleDefaultValue = () => {
    if (selected) {
      if (!fieldid) {
        return selected[name];
      } else {
        return selected[fieldid];
      }
    } else {
      return '';
    }
  };

  return (
    <Box>
      {loading ? (
        <SkeletonInput />
      ) : (
        <Autocomplete
          {...rest}
          options={data.length > 0 ? data : options}
          loading={loading}
          loadingText="Carregando..."
          value={
            !loading &&
            selected !== null &&
            selected !== undefined &&
            typeof selected === 'object' &&
            Object.keys(selected).length > 0
              ? selected
              : null
          }
          onChange={async (_, value) => {
            setSelected(value);
          }}
          isOptionEqualToValue={(option: any, value: any) =>
            option && value && option[name] == value[name]
          }
        />
      )}
      <input type="hidden" name={name} defaultValue={handleDefaultValue()} />
    </Box>
  );
}
